import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signInWithPopup, 
    signOut,
    updateProfile
} from "firebase/auth";
import { 
    collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, orderBy, writeBatch 
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage";
import { auth, googleProvider, db, storage } from "../../firebase";
import { compressImage } from "../../helper/imageCompressor";

// Friendly Error Formatter Helper
const formatAuthError = (error) => {
    const code = error?.code || error?.message || "";
    if (code.includes('auth/invalid-credential') || code.includes('auth/wrong-password') || code.includes('auth/user-not-found')) {
        return "Correo electrónico o contraseña incorrectos.";
    }
    if (code.includes('auth/email-already-in-use')) {
        return "Este correo electrónico ya está registrado. Por favor inicia sesión.";
    }
    if (code.includes('auth/weak-password')) {
        return "La contraseña debe tener al menos 6 caracteres.";
    }
    if (code.includes('auth/invalid-email')) {
        return "El correo electrónico ingresado no es válido.";
    }
    if (code.includes('auth/popup-closed-by-user')) {
        return "La ventana de inicio de sesión con Google fue cerrada.";
    }
    return error?.message || "Ocurrió un error al autenticar.";
};

// Auth Async Thunks
export const loginWithEmail = createAsyncThunk(
    'admin/loginWithEmail',
    async ({ email, password }, { rejectWithValue }) => {
        try {
            const res = await signInWithEmailAndPassword(auth, email, password);
            const userDocRef = doc(db, `Miembros/${res.user.uid}`);
            await setDoc(userDocRef, { correo: res.user.email, updatedAt: new Date().toISOString() }, { merge: true });
            return { uid: res.user.uid, email: res.user.email, displayName: res.user.displayName };
        } catch (e) {
            return rejectWithValue(formatAuthError(e));
        }
    }
);

export const registerWithEmail = createAsyncThunk(
    'admin/registerWithEmail',
    async ({ email, password, name }, { rejectWithValue }) => {
        try {
            const res = await createUserWithEmailAndPassword(auth, email, password);
            if (name) {
                try {
                    await updateProfile(res.user, { displayName: name });
                } catch (e) {
                    console.warn("Could not update display name:", e);
                }
            }
            const userDocRef = doc(db, `Miembros/${res.user.uid}`);
            await setDoc(userDocRef, { 
                nombre: name || email.split('@')[0], 
                correo: email,
                enableEmail: true,
                enableWp: true,
                enableIg: true,
                enableFb: true,
                subscription: 'free',
                createdAt: new Date().toISOString() 
            }, { merge: true });
            return { uid: res.user.uid, email: res.user.email, displayName: name || res.user.displayName };
        } catch (e) {
            return rejectWithValue(formatAuthError(e));
        }
    }
);

export const resetPassword = createAsyncThunk(
    'admin/resetPassword',
    async (email, { rejectWithValue }) => {
        try {
            await sendPasswordResetEmail(auth, email);
            return "Se ha enviado un correo para restablecer tu contraseña.";
        } catch (e) {
            return rejectWithValue(formatAuthError(e));
        }
    }
);

export const loginWithGoogle = createAsyncThunk(
    'admin/loginWithGoogle',
    async (_, { rejectWithValue }) => {
        try {
            const res = await signInWithPopup(auth, googleProvider);
            const userDocRef = doc(db, `Miembros/${res.user.uid}`);
            await setDoc(userDocRef, { 
                nombre: res.user.displayName || res.user.email?.split('@')[0] || 'Vendedor', 
                correo: res.user.email,
                updatedAt: new Date().toISOString() 
            }, { merge: true });
            return { uid: res.user.uid, email: res.user.email, displayName: res.user.displayName };
        } catch (e) {
            return rejectWithValue(formatAuthError(e));
        }
    }
);

export const logoutAdmin = createAsyncThunk(
    'admin/logout',
    async () => {
        await signOut(auth);
        return null;
    }
);

// Vendor Profile Thunks
export const fetchVendorProfile = createAsyncThunk(
    'admin/fetchVendorProfile',
    async (userID, { rejectWithValue }) => {
        try {
            const userRef = doc(db, `Miembros/${userID}`);
            const snap = await getDoc(userRef);
            if (snap.exists()) {
                return { id: snap.id, ...snap.data() };
            }
            return { id: userID, nombre: 'Vendedor', subscription: 'free' };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const updateVendorProfile = createAsyncThunk(
    'admin/updateVendorProfile',
    async ({ userID, profileData, logoFile }, { rejectWithValue }) => {
        try {
            let logoUrl = profileData.logoUrl || "";
            if (logoFile) {
                const compressedLogo = await compressImage(logoFile, { maxSizeMB: 0.2, maxWidthOrHeight: 500 });
                const logoRef = ref(storage, `Miembros/${userID}/logo/logo_${Date.now()}`);
                await uploadBytes(logoRef, compressedLogo);
                logoUrl = await getDownloadURL(logoRef);
            }

            const docRef = doc(db, `Miembros/${userID}`);
            const payload = {
                ...profileData,
                logoUrl,
                updatedAt: new Date().toISOString()
            };

            await setDoc(docRef, payload, { merge: true });
            return payload;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

// Admin Catalogs Async Thunks
export const fetchAdminCatalogs = createAsyncThunk(
    'admin/fetchCatalogs',
    async (userID, { rejectWithValue }) => {
        try {
            const catalogsRef = collection(db, `Miembros/${userID}/Catalogos`);
            const snap = await getDocs(catalogsRef);
            const catalogs = [];
            snap.forEach(d => {
                catalogs.push({ id: d.id, ...d.data() });
            });
            return catalogs;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const fetchSingleCatalog = createAsyncThunk(
    'admin/fetchSingleCatalog',
    async ({ userID, catalogID }, { rejectWithValue }) => {
        try {
            const docRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}`);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                return { id: snap.id, ...snap.data() };
            }
            return null;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const saveCatalog = createAsyncThunk(
    'admin/saveCatalog',
    async ({ userID, catalogData, coverFile }, { getState, rejectWithValue }) => {
        try {
            const state = getState().admin;
            const isPremiumV2 = state.vendorProfile?.subscription === 'premiumv2';
            
            // Check catalog creation limit if creating a new catalog
            if (!catalogData.id && !isPremiumV2 && state.catalogs.length >= 3) {
                return rejectWithValue("Límite de catálogos alcanzado (máximo 3 catálogos en el plan gratuito).");
            }

            let coverUrl = catalogData.coverUrl || catalogData.onlinePath || "";
            const catalogID = catalogData.id || doc(collection(db, `Miembros/${userID}/Catalogos`)).id;
            
            if (coverFile) {
                const compressedCover = await compressImage(coverFile, { maxSizeMB: 0.4, maxWidthOrHeight: 1200 });
                const storageRef = ref(storage, `Miembros/${userID}/${catalogID}/cover_${Date.now()}`);
                await uploadBytes(storageRef, compressedCover);
                coverUrl = await getDownloadURL(storageRef);
            }

            const docRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}`);
            const payload = {
                ...catalogData,
                id: catalogID,
                coverUrl,
                onlinePath: coverUrl,
                categoryCode: catalogData.categoryCode || 'other',
                color: catalogData.color || '#607D8B',
                updatedAt: new Date().toISOString()
            };

            await setDoc(docRef, payload, { merge: true });
            return payload;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const deleteCatalog = createAsyncThunk(
    'admin/deleteCatalog',
    async ({ userID, catalogID }, { rejectWithValue }) => {
        try {
            // 1. Delete catalog cover & images folder from Storage
            try {
                const catalogStorageRef = ref(storage, `Miembros/${userID}/${catalogID}`);
                const listRes = await listAll(catalogStorageRef);
                for (const itemRef of listRes.items) {
                    await deleteObject(itemRef).catch(() => {});
                }
            } catch (storageErr) {
                console.warn("Storage cleanup notice:", storageErr);
            }

            // 2. Delete products subcollection documents
            const productsRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos`);
            const prodSnap = await getDocs(productsRef);
            for (const pDoc of prodSnap.docs) {
                // Delete product images subcollection
                const imagesRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${pDoc.id}/Images`);
                const imgSnap = await getDocs(imagesRef);
                for (const imgDoc of imgSnap.docs) {
                    await deleteDoc(imgDoc.ref).catch(() => {});
                }
                await deleteDoc(pDoc.ref).catch(() => {});
            }

            // 3. Delete catalog document
            const docRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}`);
            await deleteDoc(docRef);
            return catalogID;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

// Admin Products Async Thunks
export const fetchAdminProducts = createAsyncThunk(
    'admin/fetchProducts',
    async ({ userID, catalogID }, { rejectWithValue }) => {
        try {
            const productsRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos`);
            const snap = await getDocs(productsRef);
            const products = await Promise.all(snap.docs.map(async (d) => {
                const data = d.data();
                let pictures = { ...(data.pictures || {}) };
                let coverUrl = data.coverUrl || data.imageUrl || data.cover || data.onlinePath || "";

                if (Array.isArray(data.pictures)) {
                    const picsMap = {};
                    data.pictures.forEach((item, i) => {
                        const url = typeof item === 'string' ? item : (item?.onlinePath || item?.url || item?.path);
                        if (url) picsMap[`pic_${i}`] = { id: `pic_${i}`, url, isCover: item?.isCover || false };
                    });
                    pictures = picsMap;
                }

                // Check subcollection Images to ensure all photos from mobile/web are loaded concurrently
                try {
                    const imagesSubref = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${d.id}/Images`);
                    const imgSnap = await getDocs(imagesSubref);
                    if (!imgSnap.empty) {
                        imgSnap.forEach(imgDoc => {
                            const imgData = imgDoc.data();
                            if (imgData) {
                                pictures[imgDoc.id] = imgData;
                                const resolved = imgData.url || imgData.onlinePath || imgData.path || imgData.downloadUrl;
                                if (resolved && (imgData.isCover || imgDoc.id === data.coverRef || !coverUrl)) {
                                    coverUrl = resolved;
                                }
                            }
                        });
                    }
                } catch (subErr) {
                    console.warn("Subcollection read notice:", subErr);
                }

                if (!coverUrl && Object.keys(pictures).length > 0) {
                    const firstKey = data.coverRef && pictures[data.coverRef] ? data.coverRef : Object.keys(pictures)[0];
                    const firstVal = pictures[firstKey];
                    coverUrl = typeof firstVal === 'string' ? firstVal : (firstVal?.url || firstVal?.onlinePath || firstVal?.path || "");
                }

                return { id: d.id, ...data, pictures, coverUrl };
            }));
            // Sort products by position (ascending) then by name
            products.sort((a, b) => (a.position || 0) - (b.position || 0));
            return products;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const saveProduct = createAsyncThunk(
    'admin/saveProduct',
    async ({ userID, catalogID, productData, imageFiles }, { getState, rejectWithValue }) => {
        try {
            const state = getState().admin;
            const isPremiumV2 = state.vendorProfile?.subscription === 'premiumv2';

            // Check product creation limit if creating a new product
            if (!productData.id && !isPremiumV2 && state.products.length >= 25) {
                return rejectWithValue("Límite de productos alcanzado (máximo 25 productos por catálogo en el plan gratuito).");
            }

            const productID = productData.id || doc(collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos`)).id;
            let pictures = { ...(productData.pictures || {}) };
            let coverRef = productData.coverRef || null;
            let coverUrl = productData.coverUrl || "";

            // Enforce max 4 images total
            const existingCount = Object.keys(pictures).length;
            const newFilesToUpload = (imageFiles || []).slice(0, Math.max(0, 4 - existingCount));

            if (newFilesToUpload.length > 0) {
                for (let i = 0; i < newFilesToUpload.length; i++) {
                    const file = newFilesToUpload[i];
                    const compressedFile = await compressImage(file, { maxSizeMB: 0.5, maxWidthOrHeight: 1200 });
                    const picID = `pic_${Date.now()}_${i}`;
                    const storageRef = ref(storage, `Miembros/${userID}/${catalogID}/${picID}`);
                    await uploadBytes(storageRef, compressedFile);
                    const url = await getDownloadURL(storageRef);
                    pictures[picID] = url;

                    if (!coverRef && (i === 0 || !coverUrl)) {
                        coverRef = picID;
                        coverUrl = url;
                    }

                    // Save image doc in Firestore subcollection
                    const imgDocRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}/Images/${picID}`);
                    await setDoc(imgDocRef, { 
                        url, 
                        isCover: coverRef === picID,
                        createdAt: new Date().toISOString() 
                    });
                }
            }

            if (!coverUrl && Object.keys(pictures).length > 0) {
                const firstKey = coverRef && pictures[coverRef] ? coverRef : Object.keys(pictures)[0];
                const firstVal = pictures[firstKey];
                coverUrl = typeof firstVal === 'string' ? firstVal : firstVal?.url || "";
            }

            const docRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}`);
            const payload = {
                ...productData,
                id: productID,
                stock: productData.stock || 'available',
                position: productData.position ?? (state.products.length + 1),
                coverRef,
                coverUrl,
                pictures,
                updatedAt: new Date().toISOString()
            };

            await setDoc(docRef, payload, { merge: true });
            return payload;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const updateProductPositions = createAsyncThunk(
    'admin/updateProductPositions',
    async ({ userID, catalogID, reorderedProducts }, { rejectWithValue }) => {
        try {
            const batch = writeBatch(db);
            const updatedProducts = reorderedProducts.map((p, idx) => {
                const newPos = idx + 1;
                const docRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${p.id}`);
                batch.update(docRef, { position: newPos });
                return { ...p, position: newPos };
            });
            await batch.commit();
            return updatedProducts;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const fetchProductImages = createAsyncThunk(
    'admin/fetchProductImages',
    async ({ userID, catalogID, productID }, { rejectWithValue }) => {
        try {
            const imagesRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}/Images`);
            const snap = await getDocs(imagesRef);
            const images = [];
            snap.forEach(d => {
                images.push({ id: d.id, ...d.data() });
            });
            return { productID, images };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const deleteProductImage = createAsyncThunk(
    'admin/deleteProductImage',
    async ({ userID, catalogID, productID, imageID, imageUrl }, { rejectWithValue }) => {
        try {
            // Delete Firestore image doc
            const imgDocRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}/Images/${imageID}`);
            await deleteDoc(imgDocRef);

            // Update parent product document to remove image from pictures map
            const prodRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}`);
            const prodSnap = await getDoc(prodRef);
            if (prodSnap.exists()) {
                const pData = prodSnap.data();
                const pictures = { ...(pData.pictures || {}) };
                delete pictures[imageID];
                let coverRef = pData.coverRef;
                if (coverRef === imageID) {
                    coverRef = Object.keys(pictures)[0] || null;
                }
                await setDoc(prodRef, { pictures, coverRef }, { merge: true });
            }

            return { productID, imageID };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const setProductCover = createAsyncThunk(
    'admin/setProductCover',
    async ({ userID, catalogID, productID, imageID }, { rejectWithValue }) => {
        try {
            const prodRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}`);
            await setDoc(prodRef, { coverRef: imageID }, { merge: true });

            // Update isCover flag on all images in subcollection
            const imagesRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}/Images`);
            const snap = await getDocs(imagesRef);
            const batch = writeBatch(db);
            snap.forEach(d => {
                batch.update(d.ref, { isCover: d.id === imageID });
            });
            await batch.commit();

            return { productID, coverRef: imageID };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const deleteProduct = createAsyncThunk(
    'admin/deleteProduct',
    async ({ userID, catalogID, productID }, { rejectWithValue }) => {
        try {
            // Delete images subcollection
            const imagesRef = collection(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}/Images`);
            const imgSnap = await getDocs(imagesRef);
            for (const imgDoc of imgSnap.docs) {
                await deleteDoc(imgDoc.ref).catch(() => {});
            }

            // Delete main product document
            const docRef = doc(db, `Miembros/${userID}/Catalogos/${catalogID}/Productos/${productID}`);
            await deleteDoc(docRef);
            return productID;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

// Admin Orders Async Thunks (Dev-Only)
export const fetchAdminOrders = createAsyncThunk(
    'admin/fetchOrders',
    async (userID, { rejectWithValue }) => {
        try {
            const ordersRef = collection(db, `Miembros/${userID}/Pedidos`);
            const q = query(ordersRef, orderBy("createdAt", "desc"));
            const snap = await getDocs(q);
            const orders = [];
            snap.forEach(d => {
                orders.push({ id: d.id, ...d.data() });
            });
            return orders;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const updateOrderStatus = createAsyncThunk(
    'admin/updateOrderStatus',
    async ({ userID, orderID, status }, { rejectWithValue }) => {
        try {
            const orderRef = doc(db, `Miembros/${userID}/Pedidos/${orderID}`);
            const payload = {
                status,
                updatedAt: new Date().toISOString()
            };
            await setDoc(orderRef, payload, { merge: true });
            return { orderID, status };
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

const adminSlice = createSlice({
    name: 'admin',
    initialState: {
        authUser: null,
        authLoading: false,
        vendorProfile: null,
        profileLoading: false,
        catalogs: [],
        catalogsLoading: false,
        activeCatalog: null,
        products: [],
        productsLoading: false,
        productImages: {}, // map of { [productID]: [imageDocs] }
        orders: [],
        ordersLoading: false,
        error: null,
        infoMessage: null
    },
    reducers: {
        setAuthUser(state, action) {
            state.authUser = action.payload;
            state.authLoading = false;
        },
        setActiveCatalog(state, action) {
            state.activeCatalog = action.payload;
        },
        clearAdminError(state) {
            state.error = null;
            state.infoMessage = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Login / Auth
            .addCase(loginWithEmail.pending, (state) => { state.authLoading = true; state.error = null; state.infoMessage = null; })
            .addCase(loginWithEmail.fulfilled, (state, action) => { state.authUser = action.payload; state.authLoading = false; })
            .addCase(loginWithEmail.rejected, (state, action) => { state.authLoading = false; state.error = action.payload; })
            .addCase(registerWithEmail.pending, (state) => { state.authLoading = true; state.error = null; state.infoMessage = null; })
            .addCase(registerWithEmail.fulfilled, (state, action) => { state.authUser = action.payload; state.authLoading = false; })
            .addCase(registerWithEmail.rejected, (state, action) => { state.authLoading = false; state.error = action.payload; })
            .addCase(resetPassword.pending, (state) => { state.authLoading = true; state.error = null; state.infoMessage = null; })
            .addCase(resetPassword.fulfilled, (state, action) => { state.authLoading = false; state.infoMessage = action.payload; })
            .addCase(resetPassword.rejected, (state, action) => { state.authLoading = false; state.error = action.payload; })
            .addCase(loginWithGoogle.pending, (state) => { state.authLoading = true; state.error = null; state.infoMessage = null; })
            .addCase(loginWithGoogle.fulfilled, (state, action) => { state.authUser = action.payload; state.authLoading = false; })
            .addCase(loginWithGoogle.rejected, (state, action) => { state.authLoading = false; state.error = action.payload; })
            .addCase(logoutAdmin.fulfilled, (state) => {
                state.authUser = null;
                state.vendorProfile = null;
                state.catalogs = [];
                state.products = [];
                state.orders = [];
                state.activeCatalog = null;
            })
            // Vendor Profile
            .addCase(fetchVendorProfile.pending, (state) => { state.profileLoading = true; })
            .addCase(fetchVendorProfile.fulfilled, (state, action) => {
                state.profileLoading = false;
                state.vendorProfile = action.payload;
            })
            .addCase(fetchVendorProfile.rejected, (state, action) => { state.profileLoading = false; state.error = action.payload; })
            .addCase(updateVendorProfile.fulfilled, (state, action) => {
                state.vendorProfile = { ...state.vendorProfile, ...action.payload };
                state.infoMessage = "Perfil actualizado exitosamente.";
            })
            // Catalogs
            .addCase(fetchAdminCatalogs.pending, (state) => { state.catalogsLoading = true; })
            .addCase(fetchAdminCatalogs.fulfilled, (state, action) => {
                state.catalogsLoading = false;
                state.catalogs = action.payload;
            })
            .addCase(fetchAdminCatalogs.rejected, (state, action) => { state.catalogsLoading = false; state.error = action.payload; })
            .addCase(fetchSingleCatalog.fulfilled, (state, action) => {
                if (action.payload) {
                    const idx = state.catalogs.findIndex(c => c.id === action.payload.id);
                    if (idx >= 0) {
                        state.catalogs[idx] = action.payload;
                    } else {
                        state.catalogs.push(action.payload);
                    }
                }
            })
            .addCase(saveCatalog.fulfilled, (state, action) => {
                const idx = state.catalogs.findIndex(c => c.id === action.payload.id);
                if (idx >= 0) {
                    state.catalogs[idx] = action.payload;
                } else {
                    state.catalogs.push(action.payload);
                }
                state.infoMessage = "Catálogo guardado exitosamente.";
            })
            .addCase(saveCatalog.rejected, (state, action) => { state.error = action.payload; })
            .addCase(deleteCatalog.fulfilled, (state, action) => {
                state.catalogs = state.catalogs.filter(c => c.id !== action.payload);
                if (state.activeCatalog?.id === action.payload) {
                    state.activeCatalog = null;
                    state.products = [];
                }
            })
            // Products
            .addCase(fetchAdminProducts.pending, (state) => { state.productsLoading = true; })
            .addCase(fetchAdminProducts.fulfilled, (state, action) => {
                state.productsLoading = false;
                state.products = action.payload;
            })
            .addCase(fetchAdminProducts.rejected, (state, action) => { state.productsLoading = false; state.error = action.payload; })
            .addCase(saveProduct.fulfilled, (state, action) => {
                const idx = state.products.findIndex(p => p.id === action.payload.id);
                if (idx >= 0) {
                    state.products[idx] = action.payload;
                } else {
                    state.products.push(action.payload);
                }
                state.infoMessage = "Producto guardado exitosamente.";
            })
            .addCase(saveProduct.rejected, (state, action) => { state.error = action.payload; })
            .addCase(updateProductPositions.fulfilled, (state, action) => {
                state.products = action.payload;
            })
            .addCase(fetchProductImages.fulfilled, (state, action) => {
                state.productImages[action.payload.productID] = action.payload.images;
            })
            .addCase(deleteProductImage.fulfilled, (state, action) => {
                const { productID, imageID } = action.payload;
                if (state.productImages[productID]) {
                    state.productImages[productID] = state.productImages[productID].filter(img => img.id !== imageID);
                }
            })
            .addCase(setProductCover.fulfilled, (state, action) => {
                const { productID, coverRef } = action.payload;
                const prod = state.products.find(p => p.id === productID);
                if (prod) {
                    prod.coverRef = coverRef;
                }
            })
            .addCase(deleteProduct.fulfilled, (state, action) => {
                state.products = state.products.filter(p => p.id !== action.payload);
            })
            // Orders (Dev-Only)
            .addCase(fetchAdminOrders.pending, (state) => { state.ordersLoading = true; })
            .addCase(fetchAdminOrders.fulfilled, (state, action) => {
                state.ordersLoading = false;
                state.orders = action.payload;
            })
            .addCase(fetchAdminOrders.rejected, (state, action) => { state.ordersLoading = false; state.error = action.payload; })
            .addCase(updateOrderStatus.fulfilled, (state, action) => {
                const ord = state.orders.find(o => o.id === action.payload.orderID);
                if (ord) {
                    ord.status = action.payload.status;
                }
            });
    }
});

export const { setAuthUser, setActiveCatalog, clearAdminError } = adminSlice.actions;
export default adminSlice.reducer;
